import { booleanAttribute, Component, effect, forwardRef, input, ViewChild } from '@angular/core'
import {
    ControlValueAccessor,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    ValidationErrors,
    Validator,
} from '@angular/forms'
import { KeyValuePipe } from '@angular/common'
import { LabelComponent } from '../label/label.component'
import { AiTextGeneratorButtonComponent } from '../../ai-text-generator/ai-text-generator-button.component'

@Component({
    selector: 'sf-input-textarea-ai',
    standalone: true,
    imports: [ReactiveFormsModule, KeyValuePipe, LabelComponent, AiTextGeneratorButtonComponent],
    templateUrl: './input-textarea-ai.component.html',
    styleUrl: './input-textarea-ai.component.scss',
    host: {
        '[class.sf-inline-control]': 'inlineLabel() && !!label()',
        '[class.sf-compact-control]': 'compact()',
        '[style.--sf-label-width]': 'labelWidth()',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputTextareaAiComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => InputTextareaAiComponent),
            multi: true,
        },
    ],
})
export class InputTextareaAiComponent implements ControlValueAccessor, Validator {
    label = input<string>('')
    placeholder = input<string>('')
    description = input<string>('')
    inputId = input<string>(`input-textarea-ai-${Math.random().toString(36).substring(2, 9)}`)
    size = input<'sm' | 'md' | 'lg'>('md')
    inlineLabel = input(false, { transform: booleanAttribute })
    compact = input(false, { transform: booleanAttribute })
    labelWidth = input('7rem')
    rows = input<number>(3)
    maxLength = input<number>(5000)
    isRequired = input<boolean>(false)
    aiInstructions = input<string>('Please rewrite this text to improve clarity and style.') // Instructions for AI generation
    aiPrompt = input<string>('') // Custom prompt for AI generation
    aiContext = input<string>('') // Additional context for AI

    @ViewChild('textarea') textareaRef!: { nativeElement: HTMLTextAreaElement }

    control = new FormControl<string>('')
    errorMessages: { [key: string]: string } = {
        required: 'This field is required',
        maxlength: `Maximum length is ${this.maxLength()} characters`,
    }

    #onChange = (_: string) => {}
    #onTouched = () => {}

    constructor() {
        effect(() => {
            if (this.maxLength() > 0) {
                this.errorMessages['maxlength'] = `Maximum length is ${this.maxLength()} characters`
            }
        })

        this.control.valueChanges.subscribe((value) => {
            this.#onChange(value || '')
            this.#onTouched()
        })
    }

    writeValue(value: string): void {
        this.control.setValue(value || '', { emitEvent: false })
    }

    registerOnChange(fn: (_: string) => void): void {
        this.#onChange = fn
    }

    registerOnTouched(fn: () => void): void {
        this.#onTouched = fn
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled ? this.control.disable() : this.control.enable()
    }

    validate(): ValidationErrors | null {
        return this.control.errors
    }

    onAiTextGenerated(generatedText: string): void {
        this.control.setValue(generatedText)
        this.control.markAsDirty()
        this.control.markAsTouched()
    }
}
